"use client";

import * as React from "react";
import Link from "next/link";
import { Gamja_Flower } from "next/font/google";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  ChevronDown,
  Sparkles,
  Shuffle,
  Clock4,
  TrendingUp,
  CalendarDays,
  ListChecks,
} from "lucide-react";
import { useIndexShell } from "@/components/article-shell/IndexShellContext";
import { REVIEW_LINKS, ReviewBanner } from "@/components/past-exam/reviewLinks";

/** 좌측 네비 제목용 브랜드(감자) 폰트 — CategoryButtons 사이드바 변형과 톤 통일. */
const gamjaFlower = Gamja_Flower({
  weight: "400",
  subsets: ["latin"],
  display: "swap",
  fallback: ["serif"],
});

/**
 * past-exam 회차 목록(루트 + 연도 인덱스 7개)용 카드형 IndexShell 슬롯 주입 래퍼 (JCG-2164).
 *
 * `/coding`·`/theory` 카탈로그(JCG-2097/2099)·요약 PDF 카탈로그(JCG-2120 SummaryPDFClient)와 동일
 * 접근: 페이지 본문(children)을 그대로 가운데에 두고, 데스크탑 IndexShell 의 좌/우/헤더 portal 슬롯에
 * 연도·회차 트리 네비(좌) / 기출 후기·학습 안내(우) / 상단 고정 제목(헤더)을 `createPortal` 주입한다.
 *
 * - 데스크탑(≥1024px)에서만 LayoutSwitch 가 IndexShell 을 렌더 → `useIndexShell().inShell=true`.
 *   모바일/태블릿(셸 미적용 → inShell=false)은 클래식 세로 스택 폴백: 좌측 트리는 본문 카드 그리드가
 *   네비 역할을 하므로 생략하고, 우측 안내(후기·학습 팁)만 본문 하단에 인라인 렌더한다.
 * - 슬롯 element 는 셸 마운트 후 ref 콜백으로 채워지므로 `slot && createPortal(...)` null 가드 필수.
 * - 각 page.tsx(서버 컴포넌트)는 본문 마크업을 children 으로, 현재 경로/제목을 props 로 넘긴다.
 */

interface PastExamRound {
  /** 회차 라벨 (예: "1회") */
  round: string;
  /** 회차 문제 풀이 페이지 경로 (예: "/past-exam/2024/2024-1") */
  href: string;
}

interface PastExamYearNav {
  /** 연도 라벨 (예: "2024년") */
  label: string;
  /** 연도 인덱스 페이지 경로 (예: "/past-exam/2024") */
  indexHref: string;
  /** 회차 목록 (1회 → N회) */
  rounds: PastExamRound[];
}

/**
 * 연도/회차 네비 SSOT — 좌측 트리 네비가 소비한다. 최신 연도 먼저.
 * 본문 카드 그리드는 각 page 의 기존 데이터(priority 등 포함)를 그대로 쓰고, 여기서는 회차 이동
 * 링크 구조만 단일 관리한다. 새 회차 추가 시 이 배열 + 해당 page 의 본문 데이터를 함께 갱신.
 */
const PAST_EXAM_NAV: PastExamYearNav[] = [
  {
    label: "2026년",
    indexHref: "/past-exam/2026",
    rounds: [
      { round: "1회", href: "/past-exam/2026/2026-1" },
      { round: "2회", href: "/past-exam/2026/2026-2" },
    ],
  },
  {
    label: "2025년",
    indexHref: "/past-exam/2025",
    rounds: [
      { round: "1회", href: "/past-exam/2025/2025-1" },
      { round: "2회", href: "/past-exam/2025/2025-2" },
      { round: "3회", href: "/past-exam/2025/2025-3" },
    ],
  },
  {
    label: "2024년",
    indexHref: "/past-exam/2024",
    rounds: [
      { round: "1회", href: "/past-exam/2024/2024-1" },
      { round: "2회", href: "/past-exam/2024/2024-2" },
      { round: "3회", href: "/past-exam/2024/2024-3" },
    ],
  },
  {
    label: "2023년",
    indexHref: "/past-exam/2023",
    rounds: [
      { round: "1회", href: "/past-exam/2023/2023-1" },
      { round: "2회", href: "/past-exam/2023/2023-2" },
      { round: "3회", href: "/past-exam/2023/2023-3" },
    ],
  },
  {
    label: "2022년",
    indexHref: "/past-exam/2022",
    rounds: [
      { round: "1회", href: "/past-exam/2022/2022-1" },
      { round: "2회", href: "/past-exam/2022/2022-2" },
      { round: "3회", href: "/past-exam/2022/2022-3" },
    ],
  },
  {
    label: "2021년",
    indexHref: "/past-exam/2021",
    rounds: [
      { round: "1회", href: "/past-exam/2021/2021-1" },
      { round: "2회", href: "/past-exam/2021/2021-2" },
      { round: "3회", href: "/past-exam/2021/2021-3" },
    ],
  },
  {
    label: "2020년",
    indexHref: "/past-exam/2020",
    rounds: [
      { round: "1회", href: "/past-exam/2020/2020-1" },
      { round: "2회", href: "/past-exam/2020/2020-2" },
      { round: "3회", href: "/past-exam/2020/2020-3" },
      { round: "4회", href: "/past-exam/2020/2020-4" },
    ],
  },
];

/** 학습 안내 항목(우측 안내) — 회차 페이지 공통 기능 요약. */
const STUDY_NOTES = [
  { icon: Shuffle, text: "기출문제를 랜덤 순서로 풀 수 있어요." },
  { icon: Clock4, text: "실시간 점수 채점으로 실력을 바로 확인해요." },
  { icon: TrendingUp, text: "2020년부터 2026년까지 회차별 기출이 모두 있어요." },
] as const;

/**
 * 좌측/인라인 네비 항목 active/비active 상태 클래스(연도·회차·전체 버튼 공용, dark 페어 포함).
 *
 * active = amber 카드(`bg-amber-50 dark:bg-amber-950/40`) + 강조 보더(`border-amber-500`, cross-mode 예외)
 * + 진한 동계열 글씨(`text-amber-700 dark:text-amber-300`). 비active = 투명 보더 + 카드 hover.
 * dark 페어 매핑은 `ui-patterns/dark-mode-colors.md` §2 표를 따른다.
 */
function navStateClass(isActive: boolean): string {
  return isActive
    ? "border-amber-500 bg-amber-50 text-amber-700 shadow-sm dark:bg-amber-950/40 dark:text-amber-300"
    : "border-border bg-card text-foreground hover:border-amber-300 hover:bg-accent dark:hover:border-amber-800";
}

/**
 * 좌측 연도/회차 트리 네비 — 연도별 회차 목록을 아코디언(disclosure)으로 접고 펴는 2단계 트리.
 *
 * 위계: 연도(연도 인덱스로 이동) → 회차(회차 문제 풀이로 이동). 연도 링크 옆의 별도 토글 버튼으로
 * 해당 연도의 회차 목록을 열고/닫는다. 상단 "전체 회차 목록"은 `/past-exam` 루트로 이동한다.
 *
 * 기본 펼침: 최신 연도(`PAST_EXAM_NAV[0]`)와, 현재 보고 있는 연도 인덱스(`currentPath === year.indexHref`).
 * 사용자가 명시적으로 토글한 연도만 `overrides`에 저장하고, 미토글 연도는 default(최신 || active)를 따른다 →
 * 경로 변경 시 active 연도가 자동으로 열린다.
 *
 * - 현재 경로와 일치하는 연도/전체 링크는 amber 강조하고 `aria-current="page"`를 제공한다.
 * - disclosure a11y: 토글 버튼 `aria-label`/`aria-expanded`/`aria-controls`, 회차 `<ul id>` 매칭,
 *   chevron `aria-hidden` 회전.
 * - 회차 링크는 현재 페이지가 아닌 문제 풀이 페이지라 강조 대상이 아니다(목록 페이지 전용 네비).
 * - 모든 인터랙티브 요소에 `focus-visible:ring-2 focus-visible:ring-amber-500`(브랜드, light/dark 공통).
 */
function PastExamNav({ currentPath }: { currentPath: string }) {
  const rootActive = currentPath === "/past-exam";
  // 사용자가 명시적으로 토글한 연도만 override로 저장. 미토글 연도는 default(최신 || active)를 따른다.
  const [overrides, setOverrides] = React.useState<Record<string, boolean>>({});
  const isYearOpen = (label: string, isLatest: boolean, isActive: boolean) =>
    overrides[label] ?? (isLatest || isActive);
  const toggleYear = (label: string, currentOpen: boolean) =>
    setOverrides((prev) => ({ ...prev, [label]: !currentOpen }));
  return (
    <nav aria-label="연도·회차별 기출문제 보기" className="flex flex-col gap-4">
      {/* 제목 — 브랜드(감자) 폰트 헤딩으로 격상(CategoryButtons 사이드바 변형과 톤 통일) */}
      <div className="flex items-center gap-2">
        <CalendarDays
          className="h-5 w-5 shrink-0 text-amber-500 dark:text-amber-400"
          aria-hidden
        />
        <h2 className={`text-lg font-semibold ${gamjaFlower.className}`}>
          기출문제 둘러보기
        </h2>
      </div>

      {/* 전체 회차 목록(루트) */}
      <Link
        href="/past-exam"
        prefetch={false}
        aria-current={rootActive ? "page" : undefined}
        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${navStateClass(
          rootActive,
        )}`}
      >
        <ListChecks
          className={`h-4 w-4 shrink-0 ${rootActive ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}
          aria-hidden
        />
        <span className="flex-1 truncate">전체 회차 + 후기</span>
        <ArrowRight
          className={`h-3.5 w-3.5 shrink-0 transition-opacity ${rootActive ? "opacity-0" : "opacity-50"}`}
          aria-hidden
        />
      </Link>

      <ul className="flex flex-col gap-2">
        {PAST_EXAM_NAV.map((year, index) => {
          const yearActive = currentPath === year.indexHref;
          const isLatest = index === 0;
          const open = isYearOpen(year.label, isLatest, yearActive);
          // 회차 패널 id — 연도 라벨에서 공백 제거(aria-controls / panel id 매칭용)
          const panelId = `past-exam-rounds-${year.label.replace(/\s/g, "")}`;
          return (
            <li key={year.indexHref} className="flex flex-col gap-1.5">
              {/* 1단계: 연도 링크 + 회차 목록 토글. active 연도는 amber 강조 */}
              <div
                className={`group flex w-full items-stretch rounded-lg border text-sm font-semibold transition-colors ${navStateClass(
                  yearActive,
                )}`}
              >
                <Link
                  href={year.indexHref}
                  prefetch={false}
                  aria-current={yearActive ? "page" : undefined}
                  className="flex min-w-0 flex-1 items-center gap-2 rounded-l-lg px-3 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full transition-colors ${
                      yearActive
                        ? "bg-amber-500 dark:bg-amber-400"
                        : "bg-muted-foreground/30 group-hover:bg-amber-400"
                    }`}
                    aria-hidden
                  />
                  <span className="flex-1 truncate">{year.label}</span>
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums ${
                      yearActive
                        ? "bg-amber-500/15 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {year.rounds.length}회차
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => toggleYear(year.label, open)}
                  aria-label={`${year.label} 회차 목록 ${open ? "접기" : "펼치기"}`}
                  aria-expanded={open}
                  aria-controls={panelId}
                  className="flex min-w-10 shrink-0 items-center justify-center rounded-r-lg border-l border-border/70 px-2 hover:bg-amber-100/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:hover:bg-amber-950/60"
                >
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 transition-transform ${
                      open ? "rotate-180" : ""
                    } ${
                      yearActive
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-muted-foreground"
                    }`}
                    aria-hidden
                  />
                </button>
              </div>

              {/*
                2단계: 회차들 — 닫힘 시 DOM에는 유지하고 display 만 끈다(aria-controls 참조 보존,
                WAI-ARIA disclosure 권장). 정적 `flex`(display:flex)가 HTML `hidden`(UA display:none)을
                덮어쓰므로 display 유틸리티 자체를 토글: open → `flex`, 닫힘 → `hidden`.
                세로 가이드라인으로 들여써 각 회차 문제 풀이로 이동.
              */}
              <ul
                id={panelId}
                className={`ml-[15px] flex-col gap-0.5 border-l border-border pl-3 ${
                  open ? "flex" : "hidden"
                }`}
              >
                {year.rounds.map((r) => (
                  <li key={r.href}>
                    <Link
                      href={r.href}
                      prefetch={false}
                      className="group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-1 focus-visible:ring-offset-background dark:hover:text-amber-300"
                    >
                      <span className="flex-1 truncate">{r.round}</span>
                      <ArrowRight
                        className="h-3.5 w-3.5 shrink-0 opacity-0 transition-[opacity,transform] group-hover:translate-x-0.5 group-hover:opacity-60 group-focus-visible:opacity-60"
                        aria-hidden
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** 우측 안내(데스크탑 셸) / 본문 하단 인라인(폴백) — 기출 후기 배너 + 학습 안내 카드. */
function PastExamAside() {
  return (
    <div className="flex flex-col gap-4">
      {/* 기출 후기 — heroImage 배너로 강조 */}
      <div>
        <div className="mb-2 flex items-center gap-1.5">
          <Sparkles
            className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400"
            aria-hidden
          />
          <h2 className="text-sm font-semibold text-amber-700 dark:text-amber-300">
            기출 후기
          </h2>
        </div>
        <div className="flex flex-col gap-3">
          {REVIEW_LINKS.map((review) => (
            <ReviewBanner key={review.href} review={review} />
          ))}
        </div>
      </div>

      {/* 학습 안내 */}
      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">학습 안내</h2>
        <ul className="flex flex-col gap-3">
          {STUDY_NOTES.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-2">
              <Icon
                className="mt-0.5 h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400"
                aria-hidden
              />
              <span className="text-xs leading-relaxed text-muted-foreground">
                {text}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

interface PastExamIndexShellProps {
  /** 현재 페이지 경로 (좌측 네비 강조용, 예: "/past-exam/2024" 또는 "/past-exam") */
  currentPath: string;
  /** 상단 고정 바(headerSlot)에 표시할 간결한 제목 */
  title: string;
  /** 상단 고정 바 부제(선택) */
  subtitle?: string;
  /** 페이지 본문 마크업 (가운데 컬럼) */
  children: React.ReactNode;
}

export default function PastExamIndexShell({
  currentPath,
  title,
  subtitle,
  children,
}: PastExamIndexShellProps) {
  const { inShell, headerSlot, leftSlot, rightSlot } = useIndexShell();

  return (
    <>
      {/* 데스크탑 셸 — 상단 고정 바(headerSlot)에 간결한 제목 portal */}
      {inShell &&
        headerSlot &&
        createPortal(
          <div className="mx-auto flex w-full max-w-[820px] flex-col px-4 py-3 sm:px-8">
            <span className="text-base font-bold">{title}</span>
            {subtitle && (
              <span className="mt-0.5 text-xs text-muted-foreground">
                {subtitle}
              </span>
            )}
          </div>,
          headerSlot,
        )}

      {/* 가운데(children) — 각 page 본문(타이틀·RedAlert·회차 카드 그리드 등) */}
      {children}

      {/* 폴백(모바일/태블릿) — 우측 안내를 본문 하단에 인라인 렌더 */}
      {!(inShell && rightSlot) && (
        <div className="mt-8 px-2">
          <PastExamAside />
        </div>
      )}

      {/* 데스크탑 셸 — 좌측 사이드바에 연도/회차 트리 네비 portal */}
      {inShell &&
        leftSlot &&
        createPortal(<PastExamNav currentPath={currentPath} />, leftSlot)}

      {/* 데스크탑 셸 — 우측 사이드바에 기출 후기·학습 안내 portal */}
      {inShell && rightSlot && createPortal(<PastExamAside />, rightSlot)}
    </>
  );
}
